import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  getConversations,
  getConversationMessages,
  sendMessage,
  reportConversation,
  Conversation,
  MessageItem,
} from '../../services/messageService';
import { useAuth } from '../../contexts/AuthContext';

const BuyerMessages: React.FC = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const toParam = searchParams.get('to');
  const furnitureParam = searchParams.get('furniture') || undefined;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const userId = user?.id;

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getConversations();
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to load conversations', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (convId: string) => {
    try {
      const data = await getConversationMessages(convId);
      const list = Array.isArray(data) ? data : [];
      setMessages(list);
      setConversations((prev) =>
        prev.map((c) =>
          c.conversationId === convId ? { ...c, unreadCount: 0 } : c
        )
      );
    } catch (e) {
      console.error('Failed to load messages', e);
    }
  }, [userId]);

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 10000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  useEffect(() => {
    if (selectedConv) {
      loadMessages(selectedConv);
      const interval = setInterval(() => {
        if (selectedConv) loadMessages(selectedConv);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConv, loadMessages]);

  useEffect(() => {
    if (!toParam || conversations.length === 0) return;
    const existing = conversations.find((c) => c.otherParty._id === toParam);
    if (existing) {
      setSelectedConv(existing.conversationId);
    }
  }, [toParam, conversations]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const content = input.trim();
    if (!content || sending) return;

    const otherId =
      conversations.find((c) => c.conversationId === selectedConv)?.otherParty._id ||
      toParam;
    if (!otherId) return;

    try {
      setSending(true);
      setSendError('');
      setInput('');
      await sendMessage({ recipient: otherId, content, furniture: furnitureParam });
      const list = await getConversations();
      const next = Array.isArray(list) ? list : [];
      setConversations(next);
      const conv = next.find((c) => c.otherParty._id === otherId);
      if (conv) {
        setSelectedConv(conv.conversationId);
        await loadMessages(conv.conversationId);
      }
    } catch (e: any) {
      setInput(content);
      setSendError(
        e.response?.data?.code === 'EDU_VERIFICATION_REQUIRED'
          ? t('messages.verifyRequired')
          : e.response?.data?.message || t('messages.sendFailed')
      );
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleReportConversation = async () => {
    if (!selectedConv) return;
    const reason = window.prompt(t('furniture.detail.reportReason'));
    if (!reason) return;
    if (reason.trim().length < 10) {
      alert(t('furniture.detail.reportTooShort'));
      return;
    }
    try {
      await reportConversation(selectedConv, reason.trim());
      alert(t('furniture.detail.reportSent'));
    } catch (error: any) {
      alert(error.response?.data?.message || t('furniture.detail.reportFailed'));
    }
  };

  const totalUnread = (Array.isArray(conversations) ? conversations : []).reduce(
    (sum, c) => sum + (c.unreadCount || 0),
    0
  );

  const canCompose = Boolean(selectedConv || toParam);
  const backLabel = i18n.language?.startsWith('zh') ? '返回会话列表' : 'Back to conversations';

  const handleBackToConversations = () => {
    setSelectedConv(null);
    setSearchParams((current) => {
      current.delete('to');
      current.delete('furniture');
      return current;
    }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('navbar.messages')}
          </h1>
          {totalUnread > 0 && (
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-800">
              {totalUnread} {t('messages.unread')}
            </span>
          )}
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="flex h-[calc(100dvh-16rem)] min-h-[22rem] md:h-[600px]">
            <div className={`${canCompose ? 'hidden md:block' : 'block'} w-full md:w-80 md:flex-none border-b md:border-b-0 md:border-r border-gray-200 overflow-y-auto`}>
              {loading ? (
                <div className="p-4 text-gray-500 text-center">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">
                  {t('messages.noConversations')}
                </div>
              ) : (
                conversations.map((conv) => (
                  <button
                    type="button"
                    key={conv.conversationId}
                    className={"w-full text-left flex items-start gap-2 p-3 cursor-pointer border-b border-gray-100 hover:bg-gray-50 " +
                      (selectedConv === conv.conversationId ? 'bg-blue-50' : '')}
                    onClick={() => setSelectedConv(conv.conversationId)}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-medium">
                      {conv.otherParty.firstName?.charAt(0)}
                      {conv.otherParty.lastName?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {conv.otherParty.firstName} {conv.otherParty.lastName}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(conv.lastMessage.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-gray-500 truncate">
                          {conv.lastMessage.sender === 'you' && 'You: '}
                          {conv.lastMessage.content}
                        </p>
                        {conv.unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium rounded-full bg-red-500 text-white">
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className={`${canCompose ? 'flex' : 'hidden md:flex'} min-w-0 flex-1 flex-col`}>
              {!canCompose ? (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  {t('messages.selectConversation')}
                </div>
              ) : (
                <>
                  <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-2 min-w-0">
                    <button
                      type="button"
                      onClick={handleBackToConversations}
                      aria-label={backLabel}
                      title={backLabel}
                      className="md:hidden text-gray-600 hover:text-gray-900 text-xl leading-none"
                    >
                      ←
                    </button>
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-medium">
                      {conversations.find((c) => c.conversationId === selectedConv)?.otherParty.firstName?.charAt(0)}
                    </div>
                    <span className="font-medium text-gray-900">
                      {conversations.find((c) => c.conversationId === selectedConv)?.otherParty.firstName || 'Seller'}{' '}
                      {conversations.find((c) => c.conversationId === selectedConv)?.otherParty.lastName || ''}
                    </span>
                    <button type="button" onClick={handleReportConversation} className="ml-auto text-sm text-red-700 underline">
                      {t('furniture.detail.reportAccount')}
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map((msg) => {
                      const isMe = msg.sender === userId;
                      return (
                        <div
                          key={msg._id}
                          className={"flex " + (isMe ? 'justify-end' : 'justify-start')}
                        >
                          <div
                            className={"max-w-xs lg:max-w-md px-3 py-2 rounded-lg " +
                              (isMe ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-900')}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <p className={"text-xs mt-1 " + (isMe ? 'text-green-100' : 'text-gray-500')}>
                              {new Date(msg.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>

                  <div className="border-t border-gray-200 p-3">
                    {sendError && (
                      <p role="alert" className="mb-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        {sendError}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        placeholder={t('messages.placeholder')}
                        rows={1}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        style={{ resize: 'none' }}
                      />
                      <button
                        onClick={handleSend}
                        disabled={sending || !input.trim()}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sending ? '...' : t('messages.send')}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerMessages;
